import { useEffect, useState } from 'react';

import {
  BebyggelseobjektDomaner,
  UpphovsmanPut,
  UpphovsmanRollTyp,
  UpphovsmanTyp,
  Upphovsorganisation,
  Upphovsperson
} from 'models';

import { isUpphovsorganisation, isUpphovsperson } from 'utilities/typeGuards';

export interface FlatUpphovsman {
  upphovsman: Upphovsorganisation | Upphovsperson;
  role: UpphovsmanRollTyp;
  displayName: string;
}

const toUpphovsmanDisplayName = (upphovsman: UpphovsmanTyp): string => {
  if (isUpphovsperson(upphovsman)) {
    return `${upphovsman.fornamn} ${upphovsman.efternamn}${
      upphovsman.fodelse_ar ? ` (f. ${upphovsman.fodelse_ar})` : ''
    }`;
  } else if (isUpphovsorganisation(upphovsman)) {
    return upphovsman.upphovsorganisationsnamn;
  }
  return upphovsman;
};

const toUpphovsman = (id: string, upphovsman: Array<UpphovsmanTyp>): UpphovsmanTyp => {
  const foundUpphovsman = upphovsman.find((upphovsman) => upphovsman.upphovsman_id === id);
  if (foundUpphovsman) {
    return foundUpphovsman;
  }

  throw new Error(`Upphovsman med id: ${id} finns inte.`);
};

const toUpphovsmanrolltyp = (type: string, upphovsmanrolltyper: UpphovsmanRollTyp[] | undefined): UpphovsmanRollTyp => {
  if (upphovsmanrolltyper) {
    const roll = upphovsmanrolltyper.find((roll: UpphovsmanRollTyp) => roll.upphovsmanrolltyp === type);
    if (roll) {
      return roll;
    }
  }
  throw new Error(`UpphovsmanRolltyp ${type} finns inte`);
};

export const useUpphovsman = (
  upphovsman: Array<{ id: string; roller: Array<string> }> | undefined,
  selectedUpphovsman: UpphovsmanTyp[],
  domaner: BebyggelseobjektDomaner | undefined
) => {
  const [list, setList] = useState<FlatUpphovsman[]>([]);

  useEffect(() => {
    if (upphovsman && domaner && selectedUpphovsman) {
      const flatList: FlatUpphovsman[] = upphovsman
        .flatMap((upphovsmanPut: UpphovsmanPut) =>
          upphovsmanPut.roller.map((roll: string) => ({
            upphovsman: toUpphovsman(upphovsmanPut.id, selectedUpphovsman),
            role: toUpphovsmanrolltyp(roll, domaner.upphovsmanrolltyper),
            displayName: toUpphovsmanDisplayName(toUpphovsman(upphovsmanPut.id, selectedUpphovsman))
          }))
        )
        .sort((a: FlatUpphovsman, b: FlatUpphovsman) => {
          const nameA = `${a.role.namn.toUpperCase()} ${a.displayName.toUpperCase()}`;
          const nameB = `${b.role.namn.toUpperCase()} ${b.displayName.toUpperCase()}`;
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });

      setList(flatList);
    }
  }, [domaner, upphovsman, selectedUpphovsman]);

  return [list];
};
